import { parsePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';
import 'moment-timezone';
import { get } from 'node-emoji';
import { USER_ROLE } from 'src/constants/common';
import { URLs } from 'src/constants/url';
import { BadgeType } from 'src/interface/common';
import { store } from 'src/redux/store';
import { logError } from 'src/services/LoggingService';
import { downloadGCSPdf } from 'src/services/QuoteService';
import { getViewerTimezone } from 'src/utils/viewerTimezone';

import defaultImage from '../assets/img/default-image.jpg';
import jpgImage from '../assets/img/JPGIcon.png';
import pdfImage from '../assets/img/pdf-icon.webp';
import pngImage from '../assets/img/png-icon.png';
import { MEASUREMENT } from '../constants/common';

const defaultScriptId = 'google-maps-script';

// Deprecated: Use appearance parameter in functions instead of calling this hook
// This function violates Rules of Hooks when called from utility functions.
// Returns the READER's clock (utils/viewerTimezone.ts), no longer the build-time organisation constant.
export const GetAppearance = () => ({ timezone: getViewerTimezone() });

// Shared address-completeness check used by both the shipper/consignee form
// (auto-opens edit) and the final booking gate (blocks submit and redirects).
// An address is considered valid when:
//   - fullAddress, latitude, longitude, postal, streetNumber are all non-empty
//   - state OR stateCode is non-empty (either alone is acceptable)
//   - country OR countryCode is non-empty (either alone is acceptable)
// Latitude/longitude `0` is treated as missing because the form layer normalizes
// missing values via `parseFloat(...) || 0`.
export interface IAddressValidityShape {
    fullAddress?: string | null;
    latitude?: number | string | null;
    longitude?: number | string | null;
    postal?: string | null;
    streetNumber?: string | null;
    city?: string | null;
    state?: string | null;
    stateCode?: string | null;
    country?: string | null;
    countryCode?: string | null;
}

const hasNonEmptyAddressValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number') return value !== 0 && Number.isFinite(value);

    return String(value).trim() !== '';
};

export const isShipperConsigneeAddressValid = (addr?: IAddressValidityShape | null): boolean => {
    if (!addr) return false;

    const requiredFields: (keyof IAddressValidityShape)[] = ['fullAddress', 'latitude', 'longitude', 'postal', 'streetNumber', 'city'];

    if (!requiredFields.every((field) => hasNonEmptyAddressValue(addr[field]))) return false;

    if (!hasNonEmptyAddressValue(addr.state) && !hasNonEmptyAddressValue(addr.stateCode)) return false;
    if (!hasNonEmptyAddressValue(addr.country) && !hasNonEmptyAddressValue(addr.countryCode)) return false;

    return true;
};

export const findServiceTypeIdBySlug = (serviceData: any[], slug: string): string | undefined => serviceData.find((val: any) => val.slug === slug)?.id;

export const ADDRESS_FIELD_LABELS: Array<{ field: string; labelKey: string }> = [
    { field: 'streetNumber', labelKey: 'QuotingHub.QuotingDashboard.Street_No' },
    { field: 'address1', labelKey: 'Common.Street' },
    { field: 'city', labelKey: 'Common.City' },
    { field: 'state', labelKey: 'Common.State' },
    { field: 'country', labelKey: 'Common.Country' },
    { field: 'postal', labelKey: 'Common.Postal_Code' },
];

export const isShipperAddressIncomplete = (address: any): boolean => {
    if (!address?.fullAddress) return false;
    if (ADDRESS_FIELD_LABELS.some(({ field }) => !address[field])) return true;
    if (address.streetNumber && !address.fullAddress.includes(address.streetNumber)) return true;

    return false;
};

export const getMissingAddressMessage = (address: any, t: (key: string, options?: Record<string, any>) => string): string => {
    if (!address?.fullAddress) return '';

    const missingLabels = ADDRESS_FIELD_LABELS.filter(({ field }) => !address[field]).map(({ labelKey }) => t(labelKey));

    if (address.streetNumber && !address.fullAddress.includes(address.streetNumber) && !missingLabels.includes(t('QuotingHub.QuotingDashboard.Street_No'))) {
        missingLabels.unshift(t('QuotingHub.QuotingDashboard.Street_No'));
    }

    if (!missingLabels.length) return '';

    return t('QuotingHub.QuotingDashboard.Address_incomplete_missing_fields', {
        count: missingLabels.length,
        missingFields: missingLabels.join(', '),
    });
};

export const getFormattedDateIgnoringTime = (
    date: any,
    format = 'MMMM Do, YYYY',
    showTodayWithDate = true,
    isTodayTomorrowOnly = false,
    ignoreDefaultDayFormat = false,
    // Short weekday ('Mon' vs 'Monday') — used by schedule date RANGES, where the full
    // weekday on both sides is too long for the card. Optional, so existing callers are
    // unaffected. Matches wal-frontend-v2's signature.
    showShortDay = false
) => {
    if (!date) {
        return '';
    }

    const dateValue = typeof date === 'string' ? date?.split('T')[0] : `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

    const appearance = GetAppearance();
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezone = appearance?.timezone || browserTimezone || 'UTC';

    const momentDate = moment(dateValue);

    if (showTodayWithDate) {
        const selectedDateString = momentDate.format('YYYY-MM-DD');

        // Safely get timezone-aware moment objects with fallback
        let todayMoment: moment.Moment;
        let yesterdayMoment: moment.Moment;
        let tomorrowMoment: moment.Moment;

        try {
            // Use moment.tz static method if available, otherwise fallback to instance method
            const momentTz = (moment as any).tz;

            if (typeof momentTz === 'function') {
                todayMoment = momentTz(timezone).startOf('day');
                yesterdayMoment = momentTz(timezone).subtract(1, 'days').startOf('day');
                tomorrowMoment = momentTz(timezone).add(1, 'days').startOf('day');
            } else if (typeof moment().tz === 'function') {
                todayMoment = moment().tz(timezone).startOf('day');
                yesterdayMoment = moment().tz(timezone).subtract(1, 'days').startOf('day');
                tomorrowMoment = moment().tz(timezone).add(1, 'days').startOf('day');
            } else {
                // Fallback to regular moment if timezone plugin not available
                todayMoment = moment().startOf('day');
                yesterdayMoment = moment().subtract(1, 'days').startOf('day');
                tomorrowMoment = moment().add(1, 'days').startOf('day');
            }
        } catch (error) {
            // If timezone is invalid, fallback to regular moment
            todayMoment = moment().startOf('day');
            yesterdayMoment = moment().subtract(1, 'days').startOf('day');
            tomorrowMoment = moment().add(1, 'days').startOf('day');
        }

        const todayString = todayMoment.format('YYYY-MM-DD');
        const yesterdayString = yesterdayMoment.format('YYYY-MM-DD');
        const tomorrowString = tomorrowMoment.format('YYYY-MM-DD');

        const currentDate = moment(selectedDateString);
        const today = moment(todayString);
        const yesterday = moment(yesterdayString);
        const tomorrow = moment(tomorrowString);

        if (currentDate.isSame(today, 'day')) {
            return isTodayTomorrowOnly ? 'Today' : `Today, ${momentDate.format(format)}`;
        } else if (currentDate.isSame(yesterday, 'day')) {
            return isTodayTomorrowOnly ? 'Yesterday' : `Yesterday, ${momentDate.format(format)}`;
        } else if (currentDate.isSame(tomorrow, 'day')) {
            return isTodayTomorrowOnly ? 'Tomorrow' : `Tomorrow, ${momentDate.format(format)}`;
        } else {
            if (ignoreDefaultDayFormat) {
                return momentDate.format(format);
            } else {
                const weekdayFormat = showShortDay ? 'ddd' : 'dddd';

                return momentDate.format(`${weekdayFormat}, ${format}`);
            }
        }
    }

    return momentDate.format(format);
};

export const getOS = () => {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

    if (macosPlatforms.indexOf(platform) !== -1) {
        return 'Mac OS';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        return 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        return 'Windows';
    } else if (/Android/.test(userAgent)) {
        return 'Android';
    } else if (/Linux/.test(platform)) {
        return 'Linux';
    }

    return 'Unknown';
};

export const getSelectBoxOptions = (
    data: Array<any>,
    value: string = 'id',
    label: string = 'name',
    isImage: boolean = false,
    imageUrl: string = 'imageUrl',
    image: string = 'image',
    members: string = 'members',
    secondLabel: any = null,
    isSort: boolean = false,
    defaultImg: any = null,
    fallbackLabel: string = ''
) => {
    if (!Array.isArray(data)) {
        data = [data];
    }

    if (isSort) {
        data.sort((a, b) => {
            const aLabel = a[label] || '';
            const bLabel = b[label] || '';

            return aLabel.localeCompare(bLabel);
        });
    }

    return data.map((dataItem: any) => {
        const primaryLabel = dataItem[label] || '';
        const secondary = secondLabel ? dataItem[secondLabel] || '' : '';
        const combinedLabel = `${primaryLabel} ${secondary}`.trim();
        const displayLabel = combinedLabel || (fallbackLabel ? dataItem[fallbackLabel] || '' : '');

        return {
            value: dataItem[value],
            label: displayLabel,
            members: dataItem[members],
            image: isImage ? (dataItem[image] ? (dataItem[imageUrl] !== null ? dataItem[imageUrl] + dataItem[image] : dataItem[image]) : defaultImg) : defaultImg,
            isImage: isImage,
        };
    });
};

export const getShortName = (name: string = '') => {
    const shortName = name
        .split(' ')
        .map((word) => word.charAt(0))
        .slice(0, 2)
        .join('');

    return shortName;
};

export const onError = (event: any) => {
    event.target.src = defaultImage;
};

export const getFormattedNumber = (number: any, isDecimal: boolean = true, isDisplayDollar = false, isParseInt = false) => {
    var options: Intl.NumberFormatOptions = {};

    if (isDecimal) {
        options = Number.isInteger(number) ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    }

    if (isParseInt) {
        number = parseInt(number);
    }
    const formattedNumber = new Intl.NumberFormat('en-US', options).format(number);

    if (isDisplayDollar) {
        if (number >= 0) {
            return formattedNumber;
        } else {
            return `-$${formattedNumber.replace('-', '')}`;
        }
    }

    return formattedNumber;
};

// Money display: thousands separators AND always two decimals ("80,850.62", "500.00").
// getFormattedNumber drops the decimals on whole numbers, which reads wrong for amounts.
export const getFormattedAmount = (value: string | number | null | undefined): string => {
    const num = Number(value);
    if (value == null || value === '' || Number.isNaN(num)) return '';

    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export const createDateWithoutTimezoneConversion = (inputDate: Date | any) => {
    if (!inputDate) return undefined;

    // Check if it's a moment object
    if (inputDate._isAMomentObject || (inputDate.year && typeof inputDate.year === 'function')) {
        return new Date(inputDate.year(), inputDate.month(), inputDate.date(), 0, 0, 0, 0);
    }

    // Check if it's a Date object
    if (inputDate instanceof Date && !isNaN(inputDate.getTime())) {
        return new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate(), 0, 0, 0, 0);
    }

    return undefined;
};

export const getDateRange = (appearance: any, range: any, allTimeStartDate = '2024-01-01T00:00:00Z') => {
    switch (range) {
        case 'last_7_days':
            return {
                start: moment().subtract(6, 'days').toDate(),
                end: moment().toDate(),
            };
        case 'last_30_days':
            return {
                start: moment().subtract(29, 'days').toDate(),
                end: moment().toDate(),
            };
        case 'last_90_days':
            return {
                start: moment().subtract(89, 'days').toDate(),
                end: moment().toDate(),
            };
        case 'last_year':
            return {
                start: moment().subtract(1, 'year').toDate(),
                end: moment().toDate(),
            };

        case 'monthly': {
            const today = moment().tz(appearance.timezone);

            return {
                start: today
                    .clone()
                    .startOf('month')
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
                end: today
                    .clone()
                    .endOf('month')
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
            };
        }

        case 'weekly': {
            const today = moment().tz(appearance.timezone);

            return {
                start: today
                    .clone()
                    .isoWeekday(0)
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
                end: today
                    .clone()
                    .isoWeekday(6)
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
            };
        }

        case 'quarterly': {
            const today = moment().tz(appearance.timezone);

            return {
                start: today
                    .clone()
                    .startOf('quarter')
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
                end: today
                    .clone()
                    .endOf('quarter')
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
            };
        }

        case 'yearly': {
            const today = moment().tz(appearance.timezone);

            return {
                start: today
                    .clone()
                    .startOf('year')
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
                end: today
                    .clone()
                    .endOf('year')
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
            };
        }
        case 'daily':
            return {
                start: moment().toDate(),
                end: moment().toDate(),
            };

        case 'all_time': {
            const today = moment().tz(appearance.timezone);
            const allTimeStartDateMoment = allTimeStartDate ? moment(allTimeStartDate, 'YYYY-MM-DD') : moment('2024-01-01', 'YYYY-MM-DD');

            return {
                start: moment(allTimeStartDateMoment, 'YYYY-MM-DD')
                    .tz(appearance.timezone)
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),

                end: today
                    .clone()
                    .set({
                        hour: today.hour(),
                        minute: today.minute(),
                        second: today.second(),
                        millisecond: today.millisecond(),
                    })
                    .toDate(),
            };
        }
        default:
            return {};
    }
};

export const getDateWithSuffixFormat = (date: string) => {
    const day = moment.utc(date, 'MMM Do YYYY').date();

    const suffixRegex = /(\d+)(st|nd|rd|th)/;

    const formattedDate = date.replace(suffixRegex, (match, p1, p2) => {
        if (day >= 11 && day <= 13) {
            return `${p1}<sup>th</sup>`;
        } else {
            switch (p2) {
                case 'st':
                    return `${p1}<sup>st</sup>`;
                case 'nd':
                    return `${p1}<sup>nd</sup>`;
                case 'rd':
                    return `${p1}<sup>rd</sup>`;
                default:
                    return `${p1}<sup>th</sup>`;
            }
        }
    });

    return formattedDate;
};

export const getFormattedDate = (
    date: any,
    format = 'MMMM Do, YYYY',
    isTimeDisplay = false,
    convertTimezone = false,
    isAppearance: any = false,
    showTodayWithDate = false,
    todayTimeFormat = 'hh:mm A',
    includeDayName = false
) => {
    if (!date) {
        return '';
    }

    const dateValue = date;
    let appearance;

    if (isAppearance) {
        appearance = isAppearance;
    } else {
        appearance = GetAppearance();
    }

    const momentDate = convertTimezone ? moment.utc(dateValue).tz(appearance?.timezone) : moment(dateValue);

    const selectedDateString = momentDate.format('YYYY-MM-DD');
    const todayString = moment().tz(appearance.timezone).startOf('day').format('YYYY-MM-DD');
    const yesterdayString = moment().tz(appearance.timezone).subtract(1, 'days').startOf('day').format('YYYY-MM-DD');
    const tomorrowString = moment().tz(appearance.timezone).add(1, 'days').startOf('day').format('YYYY-MM-DD');

    const currentDate = moment(selectedDateString);
    const today = moment(todayString);
    const yesterday = moment(yesterdayString);
    const tomorrow = moment(tomorrowString);

    if (currentDate.isSame(today, 'day')) {
        if (showTodayWithDate) return `Today, ${momentDate.format(format)}`;

        if (!isTimeDisplay) return `Today `;
        else {
            return `Today, ${momentDate.format(todayTimeFormat)}`;
        }
    } else if (currentDate.isSame(yesterday, 'day')) {
        if (showTodayWithDate) return `Yesterday, ${momentDate.format(format)}`;

        if (!isTimeDisplay) return `Yesterday`;
        else {
            return `Yesterday, ${momentDate.format(todayTimeFormat)}`;
        }
    } else if (currentDate.isSame(tomorrow, 'day')) {
        if (showTodayWithDate) return `Tomorrow, ${momentDate.format(format)}`;

        if (!isTimeDisplay) return `Tomorrow`;
        else {
            return `Tomorrow, ${momentDate.format(todayTimeFormat)}`;
        }
    } else {
        if (includeDayName) {
            return momentDate.format(`dddd, ${format}`);
        }

        return momentDate.format(format);
    }
};

export const getLabelByValue = (value: any, arr?: any) => {
    const obj: any = arr ?? MEASUREMENT;

    for (const key in obj) {
        if (obj[key].value === value) {
            return obj[key].label;
        }
    }

    return null;
};

export const capitalizeFirstLetter = (str: string) => {
    if (str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    return str;
};

export const isValidJSON = (str: any) => {
    try {
        JSON.parse(str);

        return true;
    } catch (e) {
        return false;
    }
};

export const downloadPdf = (
    url: string,
    name: string = 'invoice.pdf',
    options?: {
        isUseCache?: boolean;
        cacheBuster?: string | number;
        signal?: AbortSignal;
        headers?: Record<string, string>;
    }
): Promise<boolean> =>
    // Resolves true once the file hits the browser, false when the fetch failed — lets callers
    // show a download-in-progress indicator and surface a failure. Never rejects, so existing
    // fire-and-forget callers stay unaffected.
    downloadGCSPdf(url, options)
        .then((response: any) => {
            const blob = new Blob([response.data]);
            const pdfUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.target = '_blank';
            link.download = name;
            link.dispatchEvent(new MouseEvent('click'));
            link.remove();
            URL.revokeObjectURL(pdfUrl);

            return true;
        })
        .catch((error: any) => {
            logError(error, {
                errorMessage: error?.message || 'Error downloading PDF',
                errorLocation: 'downloadPdf',
                storeInDatabase: true,
                logToConsole: true,
            });

            return false;
        });

export const convertToGBMBKB = (val: any) => {
    const input = parseFloat(val);
    const gb = 1024 * 1024 * 1024;
    const mb = 1024 * 1024;
    const kb = 1024;
    var convertedVal: any = 0;

    if (input > gb) {
        convertedVal = `${Math.round(val / gb)} GB`;
    } else if (input > mb && input < gb) {
        convertedVal = `${Math.round(val / mb)} MB`;
    } else if (input > kb && input < mb) {
        convertedVal = `${Math.round(val / kb)} KB`;
    } else if (input < kb) {
        convertedVal = `${Math.round(input)} Bytes`;
    }

    return convertedVal;
};

export const getCountryFlag = (countryCode: any) => {
    if (!countryCode) return '';

    return String.fromCodePoint(...[...countryCode.toUpperCase()].map((char) => 127397 + char.charCodeAt()));
};

export const getUserAccountTypeColor = (accountType: string) => {
    if (accountType === USER_ROLE.ADMIN) {
        return 'success';
    } else if (accountType === USER_ROLE.USER) {
        return 'purple';
    }
};

export const isGoogleMapsReady = (): boolean => !!window.google?.maps?.places;

// Name of the global the Google script calls back into. Internal to this loader — callers get a
// promise instead of racing over window.initMap.
const GOOGLE_MAPS_CALLBACK = '__walGoogleMapsReady';

/**
 * Shared across every caller, so concurrent mounts await the SAME load instead of each injecting
 * a tag or overwriting the callback. Previously each caller assigned window.initMap itself, so
 * when two Maps-using components mounted together the second clobbered the first's callback and
 * the first never learned the script was ready. That bug was invisible while App.tsx preloaded
 * the script at boot; now the load is on demand, so the race is real and this must be shared.
 */
let googleMapsLoadPromise: Promise<void> | null = null;

/**
 * Loads the Google Maps JS API on demand and resolves once `google.maps.places` is usable.
 * Safe to call from any number of components; the script is injected at most once.
 *
 * NOTE: this is no longer called at app boot — only components that actually use Google Maps
 * (AutoCompleteGoogle, Map, ShipperConsigneeAutocomplete, MapBox) trigger it, via useGoogleMaps.
 */
export const loadGoogleMapsScript = (googleKey: string, scriptId: any = defaultScriptId): Promise<void> => {
    if (isGoogleMapsReady()) {
        // Legacy contract — some callers still rely on window.initMap being invoked.
        if (typeof window.initMap === 'function') {
            window.initMap();
        }

        return Promise.resolve();
    }

    if (googleMapsLoadPromise) {
        return googleMapsLoadPromise;
    }

    if (!googleKey) {
        return Promise.reject(new Error('Google Maps API key is missing'));
    }

    googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
        const onReady = () => {
            // Keep honouring the old window.initMap contract for any caller still using it.
            if (typeof window.initMap === 'function') {
                window.initMap();
            }

            resolve();
        };

        const onFailure = (error: Error) => {
            // Drop the cached promise so a later mount can retry instead of being stuck.
            googleMapsLoadPromise = null;
            console.error('Failed to load Google Maps script', error);
            reject(error);
        };

        const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;

        // A tag may already exist from an earlier load attempt — attach to it rather than adding
        // a second one, which would re-download the API and re-define google.maps.
        if (existingScript) {
            existingScript.addEventListener('load', onReady, { once: true });
            existingScript.addEventListener('error', () => onFailure(new Error('Google Maps script tag errored')), { once: true });

            return;
        }

        (window as any)[GOOGLE_MAPS_CALLBACK] = onReady;

        const script = document.createElement('script');
        script.id = scriptId;
        // loading=async is Google's documented pattern and silences the console warning about
        // loading the Maps JS API directly.
        script.src = `${URLs.GOOGLE_MAP_API}/js?key=${googleKey}&libraries=places,visualization&loading=async&callback=${GOOGLE_MAPS_CALLBACK}`;
        script.async = true;
        script.defer = true;

        script.onerror = function () {
            onFailure(new Error('Google Maps script failed to load'));
        };

        document.head.appendChild(script);
    });

    return googleMapsLoadPromise;
};

export const formatAddress = (address: string | false) => {
    if (typeof address === 'string') {
        address = address.replace(/Ã©/g, 'é');
        address = address.replace(/Ã‰/g, 'É');
        address = address.replace(/%/g, '');
        address = address.replace(/Ã /g, 'à');
        address = address.replace(/Ã¨/g, 'è');
        address = address.replace(/Ã¬/g, 'ì');
        address = address.replace(/Ã²/g, 'ò');
        address = address.replace(/Ã¹/g, 'ù');
        address = address.replace(/Ã‚/g, 'Â');
        address = address.replace(/Ã‹/g, 'Ë');
        address = address.replace(/ÃŠ/g, 'Ê');
        address = address.replace(/Ã‹/g, 'Ë');
        address = address.replace(/Ã¯/g, 'ï');
        address = address.replace(/Ã´/g, 'ô');
        address = address.replace(/Ã¶/g, 'ö');
        address = address.replace(/Ã»/g, 'û');
        address = address.replace(/Ã¼/g, 'ü');
        address = address.replace(/Ã§Ã /g, 'ç');
        address = address.replace(/ÃŸ/g, 'ß');
        address = address.replace(/Ã¦/g, 'æ');
        address = address.replace(/Ã¸/g, 'ø');
        address = address.replace(/Ã¥/g, 'å');
        address = address.replace(/Ã¢/g, 'â');
        address = address.replace(/Ã‘/g, 'Ñ');
        address = address.replace(/Ã¡/g, 'á');
        address = address.replace(/Ã³/g, 'ó');

        return address;
    }

    return address;
};

export const formatLastUpdate = (dateRaw: any): string => {
    const now = moment();
    const date = moment(dateRaw);
    const diffMs = now.diff(date); // Difference in milliseconds

    // Ensure difference is non-negative (handle future dates by showing absolute format)
    if (diffMs < 0) {
        // Format: 29th Apr 2025 10:28 AM
        return date.format('Do MMM YYYY h:mm A');
    }

    const diffDays = now.diff(date, 'days');

    // If 3 days or older, use absolute format
    if (diffDays >= 3) {
        // Format: 29th Apr 2025 10:28 AM
        return date.format('Do MMM YYYY h:mm A');
    }

    // --- Custom Relative Formatting Logic ---

    // Calculate precise differences using moment's duration
    const duration = moment.duration(diffMs);
    const days = Math.floor(duration.asDays()); // Whole days
    const hours = Math.floor(duration.asHours()) % 24; // Hours within the day
    const minutes = Math.floor(duration.asMinutes()) % 60; // Minutes within the hour

    if (days > 0) {
        if (hours > 0) {
            // e.g., "1 day 3 hours ago", "2 days 5 hours ago"
            return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else {
            // e.g., "1 day ago", "2 days ago"
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }
    }

    // Use total hours if no days
    const totalHours = Math.floor(duration.asHours());

    if (totalHours > 0) {
        if (minutes > 0) {
            // e.g., "1 hour 30 minutes ago", "2 hours 15 minutes ago"
            return `${totalHours} hour${totalHours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        } else {
            // e.g., "1h ago", "2h ago"
            return `${totalHours}h ago`;
        }
    }

    // Use total minutes if less than an hour
    const totalMinutes = Math.floor(duration.asMinutes());

    if (totalMinutes > 0) {
        // e.g., "15m ago" (Changed to "m" for consistency with "h")
        return `${totalMinutes}m ago`;
    }

    return 'Just now';
};

export const getFormattedDateWithTime = (
    date: any,
    format = 'MMMM Do, YYYY',
    isTimeDisplay = false,
    convertTimezone = false,
    appearance?: any,
    isTimeDisplayFormat = '[at] hh:mm A',
    isTimeFormatString = ' '
) => {
    if (!date) {
        return '-';
    }
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let momentDate = moment(date);

    if (convertTimezone) {
        momentDate = moment.utc(date).tz(appearance?.timezone ?? browserTimezone);
    }
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'days').startOf('day');
    const tomorrow = moment().add(1, 'days').startOf('day');

    if (momentDate.isSame(today, 'day')) {
        if (!isTimeDisplay) return `Today `;
        else return `Today${isTimeFormatString}${momentDate.format(`${isTimeDisplayFormat}`)}`;
    } else if (momentDate.isSame(yesterday, 'day')) {
        if (!isTimeDisplay) return `Yesterday`;
        else return `Yesterday${isTimeFormatString}${momentDate.format(`${isTimeDisplayFormat}`)}`;
    } else if (momentDate.isSame(tomorrow, 'day')) {
        if (!isTimeDisplay) return `Tomorrow`;
        else return `Tomorrow${isTimeFormatString}${momentDate.format(`${isTimeDisplayFormat}`)}`;
    } else {
        return momentDate.format(format);
    }
};

export const lastLocationUpdateToShowDotColor = (date: any): string => {
    const now = moment().utc();
    const lastUpdate = moment.utc(date);

    const differenceInHours = now.diff(lastUpdate, 'hours');

    if (differenceInHours < 6) {
        return `green`;
    } else if (differenceInHours >= 6 && differenceInHours <= 12) {
        return `yellow`;
    } else if (differenceInHours > 12) {
        return `red`;
    } else {
        return `green`;
    }
};

export const convertUtcToUserTimezone = (utcDateTime?: string, format?: string, getAsString?: boolean, userTimezone?: string) => {
    const tz = userTimezone || store.getState().basic.appearance?.timezone || 'UTC';
    if (!utcDateTime) return moment().tz(tz).toDate();
    const newDateString = moment
        .utc(utcDateTime)
        .tz(tz)
        .format(format || 'YYYY-MM-DD HH:mm');
    if (getAsString) return newDateString;
    const newDate = new Date(newDateString);

    return newDate;
};

export const convertSecondsToTimeShorted = (seconds: moment.DurationInputArg1) => {
    const duration = moment.duration(seconds, 'seconds');
    const days = Math.floor(duration.days());
    const hours = Math.floor(duration.hours());
    const minutes = Math.floor(duration.minutes());
    const secondsLeft = Math.floor(duration.seconds());

    if (days > 0) {
        return `${days}d ${hours}h`;
    } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${secondsLeft}s`;
    } else if (secondsLeft > 0) {
        return `${secondsLeft}s`;
    }

    return `0m 0s`;
};

export const getEmoji = (name: string | null) => (name ? get(name) : null);

export const formatLinearFootage = (linearFootage: number | string) => {
    if (typeof linearFootage === 'string') {
        linearFootage = Number(linearFootage);
    }

    if (isNaN(linearFootage)) {
        return 0;
    }

    return Math.ceil(linearFootage) as any;
};

// Closed-deck (Dry Van math), Flatbed, Step-Deck buckets. Trailer width drives the row-pairing math; stack ceiling drives whether height-doubled units can be stacked.
export type EquipmentBucket = 'closed-deck' | 'flatbed' | 'step-deck';

interface IBucketSpec {
    bucket: EquipmentBucket;
    trailerWidthIn: number;
    stackCeilingIn: number;
}

const BUCKET_SPECS: Record<EquipmentBucket, IBucketSpec> = {
    'closed-deck': { bucket: 'closed-deck', trailerWidthIn: 98, stackCeilingIn: 96 },
    flatbed: { bucket: 'flatbed', trailerWidthIn: 102, stackCeilingIn: 102 },
    'step-deck': { bucket: 'step-deck', trailerWidthIn: 102, stackCeilingIn: 129 },
};

// Equipment identifiers we recognize — names match via substring (case-insensitive), codes match exactly. LowBoy/RGN groups under step-deck per client direction.
const CLOSED_DECK_NAMES = ['dry van', 'reefer', 'straight truck'];
const CLOSED_DECK_CODES = ['v', 'r', 'k'];
const FLATBED_NAMES = ['flatbed'];
const FLATBED_CODES = ['f'];
const STEP_DECK_NAMES = ['step deck', 'lowboy', 'rgn'];
const STEP_DECK_CODES = ['s', 'l'];

const matchBucketName = (value: string | number | false | null | undefined): EquipmentBucket | null => {
    if (value === false || value === null || value === undefined || value === '') return null;
    const str = typeof value === 'string' ? value.toLowerCase().trim() : String(value).toLowerCase().trim();

    if (!str) return null;
    if (CLOSED_DECK_CODES.includes(str) || CLOSED_DECK_NAMES.some((n) => str.includes(n))) return 'closed-deck';
    if (FLATBED_CODES.includes(str) || FLATBED_NAMES.some((n) => str.includes(n))) return 'flatbed';
    if (STEP_DECK_CODES.includes(str) || STEP_DECK_NAMES.some((n) => str.includes(n))) return 'step-deck';

    return null;
};

// Classifies primary equipment into one of three buckets. Primaries without a built-in formula (Container, Power Only, Super B, etc.) default to closed-deck (Dry Van math).
const classifyEquipmentBucket = (primaryEquipmentType: string | number | false | null | undefined): IBucketSpec => {
    const primaryBucket = matchBucketName(primaryEquipmentType);

    if (primaryBucket) return BUCKET_SPECS[primaryBucket];

    return BUCKET_SPECS['closed-deck'];
};

// Returns the stacking ceiling (in inches) for the primary equipment. Use to enforce "Do Not Stack" toggle disable in the UI.
export const getStackingCeilingForEquipment = (primaryEquipmentType: string | number | false | null | undefined): number => classifyEquipmentBucket(primaryEquipmentType).stackCeilingIn;

// True when a single unit at the given height cannot be stacked because doubling its height exceeds the bucket's ceiling.
// `weightMeasurement` lets the caller pass heights in cm (when 'KGS') and have them auto-converted to inches before comparison.
export const isStackingBlocked = (primaryEquipmentType: string | number | false | null | undefined, height: number | string | null | undefined, weightMeasurement?: string | null): boolean => {
    const h = Number(height);

    if (!h || h <= 0) return false;

    const heightInches = weightMeasurement === 'KGS' ? h / 2.54 : h;
    const ceiling = getStackingCeilingForEquipment(primaryEquipmentType);

    return heightInches * 2 > ceiling;
};

// Returns a user-facing tooltip message explaining why stacking is blocked, or empty string when stacking is allowed.
// Numbers in the message are shown in the user's current unit (cm if metric, inches otherwise).
// In→cm display uses Math.floor so the cm value stays at-or-below the true inches value (conservative — never overstates the cm equivalent of an inches-defined cap).
export const getStackingBlockedMessage = (primaryEquipmentType: string | number | false | null | undefined, height: number | string | null | undefined, weightMeasurement?: string | null): string => {
    if (!isStackingBlocked(primaryEquipmentType, height, weightMeasurement)) return '';

    const isMetric = weightMeasurement === 'KGS';
    const ceilingInches = getStackingCeilingForEquipment(primaryEquipmentType);
    const h = Number(height);
    const combinedDisplay = isMetric ? Math.floor(h * 2) : h * 2;
    const ceilingDisplay = isMetric ? Math.floor(ceilingInches * 2.54) : ceilingInches;
    const unit = isMetric ? ' cm' : '"';

    return `Stacking blocked — combined height (${combinedDisplay}${unit}) exceeds max height (${ceilingDisplay}${unit})`;
};

// Per-group FreightCalc formula. Returns total linear feet (raw, NOT Math.ceil-rounded).
const computeLinearFootageForBucket = (mappedItems: any[], bucket: EquipmentBucket, trailerWidthIn: number, stackCeilingIn: number) => {
    let totalLinearFeet = 0;
    const isClosedDeck = bucket === 'closed-deck';

    mappedItems.forEach((group) => {
        const stackingAllowed = group.stackable && group.height * 2 <= stackCeilingIn;
        const effectiveQty = stackingAllowed ? Math.ceil(group.quantity / 2) : group.quantity;
        const palletsPerRow = Math.max(Math.floor(trailerWidthIn / group.width), 1);

        let adjFt = 0;

        if (isClosedDeck) {
            const rowsNeeded = Math.ceil(effectiveQty / palletsPerRow);

            if (group.width <= 48) {
                adjFt = effectiveQty * (group.length / 12) * 0.5;
            } else {
                adjFt = rowsNeeded * (group.length / 12);
            }
        } else {
            const unitLenFt = group.length / 12;

            if (palletsPerRow < 2) {
                adjFt = effectiveQty * unitLenFt;
            } else {
                const isOdd = effectiveQty % 2 !== 0;
                const pairedQty = isOdd ? effectiveQty - 1 : effectiveQty;
                const pairRows = Math.ceil(pairedQty / palletsPerRow);
                const oddRow = isOdd ? 1 : 0;
                adjFt = (pairRows + oddRow) * unitLenFt;
            }
        }

        totalLinearFeet += adjFt;
    });

    return totalLinearFeet;
};

export const calculateLinearFootage = (
    items: any[],
    primaryEquipmentType: string | false = false,
    isCalculateFromOrderDimensions = false,
    orderWeightMeasurement = 'LBS', // order specific as not getting from items
    weightMeasurement = undefined
) => {
    if (!items || !items.length) return 0;

    let isWeightMeasurementInKgsCms: boolean = false;

    if (isCalculateFromOrderDimensions) {
        isWeightMeasurementInKgsCms = orderWeightMeasurement === 'KGS';
    } else {
        const isCheckWeightMeasurement = weightMeasurement ?? items[0]?.weightMeasurement;
        isWeightMeasurementInKgsCms = isCheckWeightMeasurement === 'KGS';
    }

    // Conversion constants
    const INCHES_TO_CM = 2.54;
    const LBS_TO_KG = 0.453592;
    const CM_TO_INCHES = 1 / INCHES_TO_CM;

    // STEP 1: Map input items to the calculator's expected format
    const mappedItems = items.map((item) => {
        // Some callers pass isCalculateFromOrderDimensions=true with line-item data that only has itemLength (e.g. EditDetails). Fall back to the other field rather than emitting 0.
        const itemLength = isCalculateFromOrderDimensions ? Number(item.length) || Number(item.itemLength) || 0 : Number(item.itemLength) || Number(item.length) || 0;
        const itemQuantity = Number(item.handlingUnitNo) || 0;
        let itemWidth = Number(item.width) || 0;
        let itemHeight = Number(item.height) || 0;
        let itemWeight = Number(item.weight) || 0;
        let convertedItemLength = itemLength;

        // Convert from metric to imperial if needed (the calculator expects inches/lbs)
        if (isWeightMeasurementInKgsCms) {
            // Convert from cm to inches for dimensions
            itemWidth = itemWidth * CM_TO_INCHES;
            convertedItemLength = itemLength * CM_TO_INCHES;
            itemHeight = itemHeight * CM_TO_INCHES;
            // Convert from kg to lbs for weight
            itemWeight = itemWeight / LBS_TO_KG;
        }

        const mappedItem: any = {
            quantity: itemQuantity,
            width: itemWidth,
            length: convertedItemLength,
            height: itemHeight,
            weight: itemWeight,
            stackable: Boolean(!item.isStack),
        };

        return mappedItem;
    });

    const { bucket, trailerWidthIn, stackCeilingIn } = classifyEquipmentBucket(primaryEquipmentType);

    const totalLinearFeet = computeLinearFootageForBucket(mappedItems, bucket, trailerWidthIn, stackCeilingIn);

    return totalLinearFeet || 0;
};

const cmToInch = (input: any) => input * 0.393701;
const kgToLBS = (input: any) => input * 2.2046;

const calculateFreightClassForDayRoss = ({
    length,
    width,
    height,
    weight,
    unit = 'INCHES',
    weightUnit = 'KGS',
}: {
    length: any;
    width: any;
    height: any;
    weight: any;
    unit: 'INCHES' | 'CM';
    weightUnit: 'LBS' | 'KGS';
}) => {
    // Convert dimensions to inches if in cm
    if (unit === 'CM') {
        length = cmToInch(length);
        width = cmToInch(width);
        height = cmToInch(weight);
    }

    // Convert weight to pounds if in kg
    if (weightUnit === 'KGS') {
        weight = kgToLBS(weight);
    }

    // Calculate density (pounds per cubic foot)
    const volumeInCubicFeet = (length * width * height) / 1728; // 1 cubic foot = 1728 cubic inches
    const density = weight / volumeInCubicFeet;

    // Determine freight class based on density
    let freightClass;

    if (density < 1) {
        freightClass = 500;
    } else if (density < 2) {
        freightClass = 400;
    } else if (density < 4) {
        freightClass = 250;
    } else if (density < 5) {
        freightClass = 200;
    } else if (density < 6) {
        freightClass = 150;
    } else if (density < 8) {
        freightClass = 125;
    } else if (density < 10) {
        freightClass = 100;
    } else if (density < 12) {
        freightClass = 85;
    } else {
        freightClass = 50;
    }

    return freightClass;
};

function calculateFreightClass({
    weightLbs,
    lengthIn,
    widthIn,
    heightIn,
    unit = 'INCHES',
    weightUnit = 'KGS',
}: {
    weightLbs: any;
    lengthIn: any;
    widthIn: any;
    heightIn: any;
    unit: 'INCHES' | 'CM';
    weightUnit: 'LBS' | 'KGS';
}) {
    // Convert dimensions to inches if in cm
    if (unit === 'CM') {
        lengthIn = cmToInch(lengthIn);
        widthIn = cmToInch(widthIn);
        heightIn = cmToInch(heightIn);
    }

    // Convert weight to pounds if in kg
    if (weightUnit === 'KGS') {
        weightLbs = kgToLBS(weightLbs);
    }

    // Convert dimensions to cubic feet
    const volumeCubicFeet = (lengthIn * widthIn * heightIn) / 1728;
    if (volumeCubicFeet === 0) return null;

    const density = weightLbs / volumeCubicFeet;

    // Freight class thresholds (density in lbs/ft³)
    const classTable = [
        { minDensity: 50, freightClass: 50 },
        { minDensity: 35, freightClass: 55 },
        { minDensity: 30, freightClass: 60 },
        { minDensity: 22.5, freightClass: 65 },
        { minDensity: 15, freightClass: 70 },
        { minDensity: 13.5, freightClass: 77.5 },
        { minDensity: 12, freightClass: 85 },
        { minDensity: 10.5, freightClass: 92.5 },
        { minDensity: 9, freightClass: 100 },
        { minDensity: 8, freightClass: 110 },
        { minDensity: 7, freightClass: 125 },
        { minDensity: 6, freightClass: 150 },
        { minDensity: 5, freightClass: 175 },
        { minDensity: 4, freightClass: 200 },
        { minDensity: 3, freightClass: 250 },
        { minDensity: 2, freightClass: 300 },
        { minDensity: 1, freightClass: 400 },
        { minDensity: 0, freightClass: 500 },
    ];

    const matchedClass = classTable.find((row) => density >= row.minDensity);

    return matchedClass ? matchedClass.freightClass : null;
}

export const getCalculatedFreightClass = (data: any) => {
    const freightClass = calculateFreightClass({
        weightLbs: data.weight,
        lengthIn: data.itemLength,
        widthIn: data.width,
        heightIn: data.height,
        unit: data.lengthUnit,
        weightUnit: data.weightUnit,
    });

    const itemClassForDayross = calculateFreightClassForDayRoss({
        length: data.itemLength,
        width: data.width,
        height: data.height,
        weight: data.weight,
        unit: data.lengthUnit,
        weightUnit: data.weightUnit,
    });

    const responsePayload = {
        freightClass,
        itemClassForDayross,
    };

    return responsePayload;
};

export const formatDecimalNumber = (num: number | string = 0, decimalPlaces: number = 2): number => {
    if (typeof num === 'string') {
        const result = parseFloat(num).toFixed(decimalPlaces);

        return parseFloat(result);
    }

    return parseFloat(parseFloat(num.toString()).toFixed(decimalPlaces));
};

export const normalizeToOneDigitPrecision = (num: any) => {
    num = Number(num);

    if (!num || isNaN(num) || !isFinite(num)) {
        return 0;
    }

    return Number(num.toPrecision(1));
};

export const formatNumberWithRoundOff = (num: any, decimalPlaces: number = 2) => {
    num = Number(num);

    if (!num || isNaN(num) || !isFinite(num)) {
        return 0;
    }

    return Number(num.toFixed(decimalPlaces));
};

export const usePhone = () => {
    const setFormatPhone = (phone: string) => {
        try {
            if (phone) {
                const number = parsePhoneNumber(phone, 'US');

                return number.isValid() ? number.formatNational() : phone;
            }

            return phone;
        } catch (error: any) {
            return phone;
        }
    };

    const getFormatPhone = (phone: string) => {
        phone = phone && phone.includes(',') ? phone.split(',')[0] : phone;

        try {
            const number = parsePhoneNumber(phone, 'US');
            let formattedNumber = phone;

            if (number.isValid()) {
                formattedNumber = number.getType() === 'TOLL_FREE' ? number.formatInternational() : number.formatNational();
            }

            return formattedNumber.replace('+', '').replace('(', '').replace(')', '').split(' ').join('-');
        } catch (error: any) {
            return phone;
        }
    };

    const getPhone = (phone: string) => {
        phone = phone && phone.includes(',') ? phone.split(',')[0] : phone;

        try {
            const number = parsePhoneNumber(phone, 'US');

            return number.isValid() ? number.number : phone;
        } catch (error: any) {
            return phone;
        }
    };

    const getFormatPhoneWithPlus = (phone: any) => {
        if (!phone) return '';

        try {
            const cleanedPhone = phone.toString().replace(/[^\d+]/g, '');

            let phoneNumber = parsePhoneNumberFromString(cleanedPhone);

            if (!phoneNumber || !phoneNumber.isValid()) {
                if (cleanedPhone.startsWith('+')) {
                    phoneNumber = parsePhoneNumber(cleanedPhone);
                } else {
                    phoneNumber = parsePhoneNumber(cleanedPhone, 'US');

                    if (!phoneNumber.isValid()) {
                        phoneNumber = parsePhoneNumber(cleanedPhone, 'CA');
                    }
                }
            }

            if (phoneNumber && phoneNumber.isValid()) {
                const countryCode = phoneNumber.countryCallingCode;
                const isUSOrCA = countryCode === '1';

                if (isUSOrCA && phoneNumber.nationalNumber.length === 10) {
                    return `+${countryCode} (${phoneNumber.nationalNumber.slice(0, 3)}) ${phoneNumber.nationalNumber.slice(3, 6)}-${phoneNumber.nationalNumber.slice(6)}`;
                }

                return phoneNumber.formatInternational();
            }

            if (cleanedPhone.length === 10 && !cleanedPhone.startsWith('+')) {
                return `+1 (${cleanedPhone.slice(0, 3)}) ${cleanedPhone.slice(3, 6)}-${cleanedPhone.slice(6)}`;
            }

            return phone;
        } catch (error: any) {
            return phone;
        }
    };

    return { setFormatPhone, getFormatPhone, getPhone, getFormatPhoneWithPlus };
};

export const getPriorityPickupDateTime = (activeLegData: any, appearance: any) => {
    // 1. Carrier Pickup Appointment (highest priority)
    if (activeLegData?.carrierPickupAppointmentStartDateTime) {
        const hasWindow = activeLegData?.carrierPickupAppointmentHasWindow;

        if (hasWindow && activeLegData?.carrierPickupAppointmentEndDateTime) {
            return activeLegData.carrierPickupAppointmentEndDateTime;
        }

        return activeLegData.carrierPickupAppointmentStartDateTime;
    }

    // 2. Shipper Pickup Appointment
    if (activeLegData?.shipperAppointmentStartDateTime) {
        const hasWindow = activeLegData?.shipperAppointmentHasWindow;

        if (hasWindow && activeLegData?.shipperAppointmentEndDateTime) {
            return activeLegData.shipperAppointmentEndDateTime;
        }

        return activeLegData.shipperAppointmentStartDateTime;
    }

    // 3. Expected Pickup Date (fallback)
    if (activeLegData?.expectedPickupTime) {
        return moment.utc(activeLegData.expectedPickupTime).tz(appearance?.timezone).startOf('day').hour(12).utc().format('YYYY-MM-DD HH:mm:ss');
    }
};

export const getPriorityDeliveryDateTime = (activeLegData: any, appearance: any) => {
    // 1. Carrier Delivery Appointment (highest priority)
    if (activeLegData?.carrierDeliveryAppointmentStartDateTime) {
        const hasWindow = activeLegData?.carrierDeliveryAppointmentHasWindow;

        if (hasWindow && activeLegData?.carrierDeliveryAppointmentEndDateTime) {
            return activeLegData.carrierDeliveryAppointmentEndDateTime;
        }

        return activeLegData.carrierDeliveryAppointmentStartDateTime;
    }

    // 2. Consignee Delivery Appointment
    if (activeLegData?.deliveryAppointmentStartDateTime) {
        const hasWindow = activeLegData?.deliveryAppointmentHasWindow;

        if (hasWindow && activeLegData?.deliveryAppointmentEndDateTime) {
            return activeLegData.deliveryAppointmentEndDateTime;
        }

        return activeLegData.deliveryAppointmentStartDateTime;
    }

    // 3. Expected Delivery Date (fallback)
    if (activeLegData?.expectedEstimatedDeliveryTime) {
        // Read as UTC, extract date, convert to user timezone, set to 3 PM in that timezone, then convert back to UTC

        return moment.utc(activeLegData.expectedEstimatedDeliveryTime).tz(appearance?.timezone).startOf('day').hour(15).utc().format('YYYY-MM-DD HH:mm:ss');
    }
};

export const formatTimeInput = (timeValue: string | null | undefined, previousValue: string | null | undefined = ''): string => {
    timeValue = timeValue ?? '';
    previousValue = previousValue ?? '';

    const cleanedValue = timeValue.replace(/[^0-9:]/g, '');
    const parts = cleanedValue.split(':');
    let hours = parts[0] || '';
    let minutes = parts[1] || '';

    if (hours.length > 2) hours = hours.slice(0, 2);
    if (minutes.length > 2) minutes = minutes.slice(0, 2);

    if (hours.length === 1 && parseInt(hours) > 2) {
        hours = `0${hours}`;

        return `${hours}:`;
    }

    if (hours.length === 2) {
        const hourNum = parseInt(hours);

        if (hourNum < 0 || hourNum > 23) {
            hours = hours.slice(0, 1);
        }
    }

    if (minutes.length === 2) {
        const minuteNum = parseInt(minutes);

        if (minuteNum < 0 || minuteNum > 59) {
            minutes = minutes.slice(0, 1);
        }
    }

    const isTypingForward = timeValue.length > previousValue.length;
    const previousHadMinutes = previousValue.includes(':') && previousValue.split(':')[1]?.length > 0;

    if (hours.length === 1 && minutes === '00') {
        return `${hours}`;
    }

    if (isTypingForward && cleanedValue.endsWith(':') && hours.length === 2 && minutes === '' && hours !== '00' && !previousHadMinutes) {
        const hourNum = parseInt(hours);

        if (hourNum >= 1 && hourNum <= 23) {
            return `${hours}:00`;
        }
    }

    if (cleanedValue.length === 2 && !cleanedValue.includes(':')) {
        return `${hours}:`;
    } else if (parts.length === 2) {
        return `${hours}:${minutes}`;
    } else {
        return hours;
    }
};

export const generateUnitSummary = (items: Array<any>, unitSummaryJoinLabel = ' & ') => {
    if (!Array.isArray(items)) {
        return '';
    }
    const unitSums: any = {};

    items?.forEach((item: any) => {
        const unit = item?.unit?.toLowerCase();
        const handlingUnitNo = item?.handlingUnitNo || 0;

        unitSums[unit] = (unitSums[unit] || 0) + handlingUnitNo;
    });

    const unitSummary = Object?.entries(unitSums)?.map(([unit, sum]: any) => `${sum} ${unit}${sum > 1 ? 's' : ''}`);

    return unitSummary?.join(unitSummaryJoinLabel);
};

export const getFileExtensionTypeImage = (extension: string) => {
    const normalizedExtension = extension?.toLowerCase();

    if (normalizedExtension === 'jpg' || normalizedExtension === 'jpeg') {
        return jpgImage;
    } else if (normalizedExtension === 'png') {
        return pngImage;
    } else if (normalizedExtension === 'pdf') {
        return pdfImage;
    } else {
        return pdfImage; // Default to PDF icon
    }
};

export const formatNumber = (value: any, isIconShow: boolean = true): string => {
    const num = Number(value);

    if (isNaN(num) || num <= 0) {
        return '$0';
    }

    const units = ['', 'K', 'M', 'B'];
    let unitIndex = 0;
    let formattedNum = num;

    while (formattedNum >= 1000 && unitIndex < units.length - 1) {
        formattedNum /= 1000;
        unitIndex++;
    }

    return isIconShow ? `$${formattedNum.toFixed(1).replace(/\.0$/, '')}${units[unitIndex]}` : `${formattedNum.toFixed(1).replace(/\.0$/, '')}${units[unitIndex]}`;
};

export const copyToClipboard = (text: string) => {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text).catch(() => {
            const el = document.createElement('textarea');
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        });
    } else {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }
};

export const getPredictiveIndexTitleAndColor = (predictiveLocationIndex: string | null | undefined): { titleKey: string; color: string; badgeType: BadgeType; reasonKey: string } => {
    switch (predictiveLocationIndex) {
        case 'on_track':
            return { titleKey: 'Radar.PLI_Title_On_Track', badgeType: 'success', color: '#17b26a', reasonKey: 'Radar.PLI_Reason_On_Track' };
        case 'at_risk':
            return { titleKey: 'Radar.PLI_Title_At_Risk', badgeType: 'yellow', color: '#fdb022', reasonKey: 'Radar.PLI_Reason_At_Risk' };
        case 'delayed':
            return { titleKey: 'Radar.PLI_Title_Delayed', badgeType: 'warning', color: '#f38744', reasonKey: 'Radar.PLI_Reason_Delayed' };
        case 'critical_delay':
            return { titleKey: 'Radar.PLI_Title_Critical_Delay', badgeType: 'red', color: '#f04437', reasonKey: 'Radar.PLI_Reason_Critical_Delay' };
        default:
            return { titleKey: 'Radar.PLI_Title_On_Track', badgeType: 'success', color: '#17b26a', reasonKey: 'Radar.PLI_Reason_On_Track' };
    }
};

export const getTimeBasedGreeting = (t: (key: string) => string) => {
    const currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour < 12) {
        return t('Auth.Good_Morning');
    } else if (currentHour >= 12 && currentHour < 17) {
        return t('Auth.Good_Afternoon');
    } else {
        return t('Auth.Good_Evening');
    }
};

export const normalizeTimeValue = (raw: string | null | undefined): string => {
    const digits = (raw || '').replace(/[^0-9]/g, '');
    if (!digits) return '';

    let hours: string;
    let minutes: string;

    if (digits.length <= 2) {
        hours = digits;
        minutes = '00';
    } else if (digits.length === 3) {
        hours = digits.slice(0, 2);
        const mTens = digits[2];
        // Minute tens 6-9 means user typed a single digit ≥6 → treat as 0X (e.g. 6 → 06)
        minutes = +mTens > 5 ? `0${mTens}` : `${mTens}0`;
    } else {
        hours = digits.slice(0, 2);
        minutes = digits.slice(2, 4);

        // When m > 59 the user typed a single digit ≥6 for minute-tens while
        // the old ones digit was still in the field (e.g. "04:00" → type "6" → "04:60").
        // Re-interpret the tens digit as 0X just like the 3-digit branch does.
        if (+minutes > 59) {
            const mTens = digits[2];
            minutes = +mTens > 5 ? `0${mTens}` : `${mTens}0`;
        }
    }

    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (h < 0 || h > 23 || m < 0 || m > 59) return '';

    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const getTimeMaskBlurHandler = (e: any, HandleChange: any) => {
    const normalized = normalizeTimeValue(e.target.value);
    const current = (e.target.value || '').replace(/[^0-9]/g, '');

    if (current && normalized !== e.target.value) {
        HandleChange({ target: { value: normalized } });
    }
};

export const extractCityOrStateName = (value?: string | null): string => {
    if (!value) return '';

    return String(value).split(',')[0]?.trim() ?? '';
};

export const smoothScrollIntoView = (element: Element, options?: ScrollIntoViewOptions): void => {
    try {
        element.scrollIntoView(options);
    } catch {
        // Fallback — boolean false aligns to the bottom of the visible area
        element.scrollIntoView(false);
    }
};

/**
 * Flatten a react-hook-form `errors` object into leaf field paths ("deliveryDate",
 * "dimensions.0.weight"). RHF nests array/object field errors, so Object.keys(errors) alone
 * only yields the top-level container ("dimensions") which never matches a DOM input.
 */
export const collectFormErrorPaths = (errors: any, prefix = ''): string[] => {
    if (!errors || typeof errors !== 'object') return [];

    return Object.keys(errors).reduce<string[]>((paths, key) => {
        const value = errors[key];

        if (!value || typeof value !== 'object') return paths;

        const path = prefix ? `${prefix}.${key}` : key;

        // A leaf FieldError carries `message`/`type`; anything else is a nested group to recurse into.
        if ('message' in value || 'type' in value) {
            paths.push(path);
        } else {
            paths.push(...collectFormErrorPaths(value, path));
        }

        return paths;
    }, []);
};

/**
 * Scroll to the first form field that failed validation, so a blocked "Next" never leaves the
 * user staring at an unchanged screen with the offending field parked off-viewport.
 *
 * Fields are found by `name`/`id` attribute rather than React refs: the stepper's steps mount and
 * unmount as the user moves around, so refs captured elsewhere would be stale. The target is the
 * one that comes FIRST in document order — not first in the errors object, whose key order follows
 * the yup schema and does not necessarily match what the user sees down the page.
 *
 * requestAnimationFrame defers the measurement until after the render that paints the error states.
 */
/**
 * Resolve one RHF field path to the element that should be scrolled to.
 *
 * Tries, in order: the input's own `name`, its `id`, then an explicit `data-field-anchor` placed on
 * a wrapping section. If nothing matches, the last path segment is dropped and the lookup repeats —
 * so "consigneeAddress.fullAddress" falls back to "consigneeAddress", and "dimensions.0.weight"
 * falls back to "dimensions.0" then "dimensions".
 *
 * The fallbacks matter because several required fields have no queryable input of their own: an
 * address is a composite object validated field-by-field, and button groups / checkbox grids render
 * no element carrying the form field's name. Those sections get a `data-field-anchor` instead.
 */
const resolveFormErrorElement = (fieldPath: string): HTMLElement | null => {
    const segments = fieldPath.split('.');

    for (let end = segments.length; end > 0; end--) {
        const path = segments.slice(0, end).join('.');

        try {
            const el =
                document.querySelector<HTMLElement>(`[name="${path}"]`) ||
                document.querySelector<HTMLElement>(`[id="${path}"]`) ||
                document.querySelector<HTMLElement>(`[data-field-anchor~="${path}"]`);

            if (el) return el;
        } catch {
            // A path that isn't a valid selector must not take the whole step down.
            return null;
        }
    }

    return null;
};

export const scrollToFirstFormError = (errorFields: string[]): void => {
    if (!errorFields?.length) return;

    requestAnimationFrame(() => {
        const elements = errorFields.map(resolveFormErrorElement).filter((el): el is HTMLElement => !!el);

        if (!elements.length) return;

        const firstInDocument = elements.reduce((earliest, candidate) =>
            // eslint-disable-next-line no-bitwise
            earliest.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_PRECEDING ? candidate : earliest
        );

        smoothScrollIntoView(firstInDocument, { behavior: 'smooth', block: 'center' });
    });
};

/**
 * Digits-only MC number for display as "MC #<digits>". Stored values (carrier.mcId and the
 * Organization Settings MC) sometimes carry an "MC" prefix ("MC000862"); rendering them under a
 * hardcoded "MC #" label would double it. Same helper as wal-frontend-v2's carrier.utils.ts so
 * WALHQ and the portal show the identical string. Returns null when nothing usable remains.
 */
export const extractMcNumber = (mcId?: string | number | null): string | null => {
    if (!mcId) return null;

    return String(mcId).replace(/\D/g, '') || null;
};
